/**
 * The rules half of the character creation wizard, kept out of the component
 * so it can be tested without a DOM.
 *
 * The wizard used to carry its own 5e class list, its own hit point formula and
 * a fixed five-step tuple with a class step in it. Each of those made one kind
 * of system impossible to create a character for: a classless one like Trophy
 * Dark could not get past the class step at all. Everything here asks the
 * adapter instead, and treats "the system has none" as an ordinary answer.
 *
 * Every function takes the adapter as an argument rather than reading the
 * active one, so a test can hand in 5e, Doomsworn or a rules document directly.
 */

import {
  abilityStorageKey,
  toSystemAbilities,
  type AbilityDefinition,
  type AbilityScores,
  type ClassDefinition,
  type ClassListContext,
  type GameSystemAdapter,
} from '@machina/core';

// =============================================================================
// STEPS
// =============================================================================

export type CreationStep = 'basics' | 'class' | 'abilities' | 'combat' | 'review';

export const STEP_TITLES: Record<CreationStep, string> = {
  basics: 'Basic Info',
  class: 'Class',
  abilities: 'Ability Scores',
  combat: 'Combat Stats',
  review: 'Review',
};

/**
 * The wizard's steps for a system with this many classes and abilities.
 *
 * A step with nothing to choose is dropped rather than shown empty. An empty
 * class step would block Next with no way forward, and an empty ability step
 * would ask the player to assign values to nothing.
 */
export function creationSteps(shape: { classCount: number; abilityCount: number }): CreationStep[] {
  const steps: CreationStep[] = ['basics'];
  if (shape.classCount > 0) steps.push('class');
  if (shape.abilityCount > 0) steps.push('abilities');
  steps.push('combat', 'review');
  return steps;
}

// =============================================================================
// CLASSES
// =============================================================================

/**
 * Classes the system offers for this campaign's setting. Empty for a system
 * without classes, or when no system is active.
 */
export function classesFor(
  adapter: GameSystemAdapter | undefined,
  context?: ClassListContext,
): ClassDefinition[] {
  return adapter?.getClasses(context) ?? [];
}

/**
 * The class the wizard starts on: Fighter when the list has one, since that is
 * what the wizard has always opened on in 5e, otherwise the first class.
 * Null when there are no classes to choose.
 */
export function defaultClassOf(classes: ClassDefinition[]): ClassDefinition | null {
  return classes.find(c => c.id === 'fighter') ?? classes[0] ?? null;
}

/**
 * A class's saving throws as the keys a stored character uses.
 *
 * Systems disagree on how they name them: 5e's class table says `strength`,
 * Doomsworn's Roles say `str`. The sheet reads storage keys, so a short id is
 * mapped through the system's ability list. A name that matches no ability is
 * kept as written rather than dropped, because dropping it would quietly remove
 * a proficiency the system granted.
 */
export function saveKeysOf(
  cls: ClassDefinition | null,
  abilities: AbilityDefinition[],
): string[] {
  if (!cls?.savingThrows) return [];
  return cls.savingThrows.map(save => {
    const ability = abilities.find(a => a.id === save || abilityStorageKey(a) === save);
    return ability ? abilityStorageKey(ability) : save;
  });
}

// =============================================================================
// NUMBERS THE SYSTEM OWNS
// =============================================================================

/**
 * Starting maximum health, as the system rules it, or undefined when it does
 * not say.
 *
 * Undefined is passed through as-is. The wizard shows no suggestion in that
 * case and the player types the number, since any figure made up here would
 * look exactly like one the system ruled.
 */
export function startingHealthFor(
  adapter: GameSystemAdapter | undefined,
  choice: { abilityScores: AbilityScores; classId?: string; level?: number },
): number | undefined {
  const surface = adapter?.characterSurface;
  if (!adapter || !surface?.getStartingHealth) return undefined;

  const value = surface.getStartingHealth({
    name: '',
    abilities: toSystemAbilities(choice.abilityScores, adapter.getAbilities()),
    classId: choice.classId,
    level: choice.level,
  });
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Proficiency bonus at this level, or undefined for a system without one or a
 * character without a level.
 */
export function proficiencyBonusFor(
  adapter: GameSystemAdapter | undefined,
  level: number | undefined,
): number | undefined {
  if (level === undefined) return undefined;
  return adapter?.getProficiencyBonus?.(level);
}

/** Whether the system gives characters a race or ancestry to pick. */
export function systemUsesRaces(adapter: GameSystemAdapter | undefined): boolean {
  return adapter?.rules.characterTemplate.usesRaces ?? true;
}

/** Whether the system has an armour class for the wizard to ask about. */
export function systemUsesArmorClass(adapter: GameSystemAdapter | undefined): boolean {
  return (adapter?.rules.characterTemplate.defenseModel ?? 'armor-class') === 'armor-class';
}

/** Highest level the level slider should reach. */
export function levelCapOf(adapter: GameSystemAdapter | undefined): number {
  return adapter?.rules.characterTemplate.levelCap ?? 20;
}
